import { AssetNames } from "@utils/types";
import {
  BaseCharacterGender,
  BaseCharacterEyes,
  BaseCharacterClothing,
  BaseCharacterPants,
  BaseCharacterShirt,
  BaseCharacterHairBase,
  BaseCharacterHairBack,
  BaseCharacterHairFringe,
  BaseCharacterHairSide,
  BaseCharacterHairTail,
  BaseCharacterRace,
  BaseCharacterMoustache,
  BaseCharacterBeard,
} from "mmo-common";

export const BASE_CHARACTER_ASSET_MAPPING: {
  gendered: {
    [g in BaseCharacterGender]: {
      eyes: {
        [v in BaseCharacterEyes]: AssetNames;
      };
      skinShadow: AssetNames;
      clothing: {
        [v in Exclude<
          BaseCharacterClothing,
          BaseCharacterClothing.None
        >]: AssetNames;
      };
      pants: {
        [v in Exclude<BaseCharacterPants, BaseCharacterPants.None>]: AssetNames;
      };
      defaultPants: AssetNames;
      defaultShirt: AssetNames | null;
      shirt: {
        [v in Exclude<BaseCharacterShirt, BaseCharacterShirt.None>]: AssetNames;
      };
    };
  };
  common: {
    hairBase: {
      [v in Exclude<
        BaseCharacterHairBase,
        BaseCharacterHairBase.None
      >]: AssetNames;
    };
    hairBack: {
      [v in Exclude<
        BaseCharacterHairBack,
        BaseCharacterHairBack.None
      >]: AssetNames;
    };
    hairFringe: {
      [v in Exclude<
        BaseCharacterHairFringe,
        BaseCharacterHairFringe.None
      >]: AssetNames;
    };
    hairSide: {
      [v in Exclude<
        BaseCharacterHairSide,
        BaseCharacterHairSide.None
      >]: AssetNames;
    };
    hairTail: {
      [v in Exclude<
        BaseCharacterHairTail,
        BaseCharacterHairTail.None
      >]: AssetNames;
    };
    ears: {
      [r in BaseCharacterRace]: AssetNames;
    };
    raceTexture: {
      [r in BaseCharacterRace]:
        | {
            [g in BaseCharacterGender]: AssetNames;
          }
        | null;
    };
    moustache: {
      [v in Exclude<
        BaseCharacterMoustache,
        BaseCharacterMoustache.None
      >]: AssetNames;
    };
    beard: {
      [v in Exclude<BaseCharacterBeard, BaseCharacterBeard.None>]: AssetNames;
    };
  };
} = {
  gendered: {
    [BaseCharacterGender.Male]: {
      eyes: {
        [BaseCharacterEyes.Normal]: "baseCharacterMaleEyesNormal",
        [BaseCharacterEyes.Determined]: "baseCharacterMaleEyesDetermined",
        [BaseCharacterEyes.Curious]: "baseCharacterMaleEyesCurious",
        [BaseCharacterEyes.Cool]: "baseCharacterMaleEyesCool",
        [BaseCharacterEyes.Kind]: "baseCharacterMaleEyesKind",
        [BaseCharacterEyes.Beautiful]: "baseCharacterMaleEyesBeautiful",
        [BaseCharacterEyes.Sharp]: "baseCharacterMaleEyesSharp",
        [BaseCharacterEyes.Almond]: "baseCharacterMaleEyesAlmond",
        [BaseCharacterEyes.Fierce]: "baseCharacterMaleEyesFierce",
        [BaseCharacterEyes.Cat]: "baseCharacterMaleEyesCat",
      },
      skinShadow: "baseCharacterMaleSkinShadows",
      clothing: {
        [BaseCharacterClothing.Normal]: "baseCharacterMaleClothing",
      },
      pants: {
        [BaseCharacterPants.Assassins]: "malePantsAssassins",
      },
      defaultPants: "malePantsBase",
      shirt: {
        [BaseCharacterShirt.Assassins]: "maleShirtAssassins",
        [BaseCharacterShirt.Mage]: "maleShirtMage",
        [BaseCharacterShirt.Warrior]: "maleShirtWarrior",
      },
      defaultShirt: null,
    },
    [BaseCharacterGender.Female]: {
      eyes: {
        [BaseCharacterEyes.Normal]: "baseCharacterFemaleEyesNormal",
        [BaseCharacterEyes.Determined]: "baseCharacterFemaleEyesDetermined",
        [BaseCharacterEyes.Curious]: "baseCharacterFemaleEyesCurious",
        [BaseCharacterEyes.Cool]: "baseCharacterFemaleEyesCool",
        [BaseCharacterEyes.Kind]: "baseCharacterFemaleEyesKind",
        [BaseCharacterEyes.Beautiful]: "baseCharacterFemaleEyesBeautiful",
        [BaseCharacterEyes.Sharp]: "baseCharacterFemaleEyesSharp",
        [BaseCharacterEyes.Almond]: "baseCharacterFemaleEyesAlmond",
        [BaseCharacterEyes.Fierce]: "baseCharacterFemaleEyesFierce",
        [BaseCharacterEyes.Cat]: "baseCharacterFemaleEyesCat",
      },
      skinShadow: "baseCharacterFemaleSkinShadows",
      clothing: {
        [BaseCharacterClothing.Normal]: "baseCharacterFemaleClothing",
      },
      pants: {
        [BaseCharacterPants.Assassins]: "femalePantsAssassins",
      },
      defaultPants: "femalePantsBase",
      shirt: {
        [BaseCharacterShirt.Assassins]: "femaleShirtAssassins",
        [BaseCharacterShirt.Mage]: "femaleShirtMage",
        [BaseCharacterShirt.Warrior]: "femaleShirtWarrior",
      },
      defaultShirt: "femaleShirtBase",
    },
  },
  common: {
    hairBase: {
      [BaseCharacterHairBase.Normal]: "baseCharacterHairBaseNormal",
      [BaseCharacterHairBase.Mohawk]: "baseCharacterHairBaseMohawk",
      [BaseCharacterHairBase.Undercut]: "baseCharacterHairBaseUndercut",
      [BaseCharacterHairBase.Spiky]: "baseCharacterHairBaseSpiky",
    },
    hairBack: {
      [BaseCharacterHairBack.Short]: "baseCharacterHairBackShort",
      [BaseCharacterHairBack.Long]: "baseCharacterHairBackLong",
    },
    hairFringe: {
      [BaseCharacterHairFringe.Normal]: "baseCharacterHairFringeNormal",
      [BaseCharacterHairFringe.Straight]: "baseCharacterHairFringeStraight",
      [BaseCharacterHairFringe.MiddlePart]: "baseCharacterHairFringeMiddlePart",
      [BaseCharacterHairFringe.MiddlePartThick]:
        "baseCharacterHairFringeMiddlePartThick",
      [BaseCharacterHairFringe.StraightBangs]:
        "baseCharacterHairFringeStraightBangs",
      [BaseCharacterHairFringe.SidePart]: "baseCharacterHairFringeSidePart",
      [BaseCharacterHairFringe.Curtains]: "baseCharacterHairFringeCurtains",
      [BaseCharacterHairFringe.SideStraightPart]:
        "baseCharacterHairFringeSideStraightPart",
      [BaseCharacterHairFringe.FluffyBangs]:
        "baseCharacterHairFringeFluffyBangs",
    },
    hairSide: {
      [BaseCharacterHairSide.Normal]: "baseCharacterHairSideNormal",
      [BaseCharacterHairSide.Long]: "baseCharacterHairSideLong",
    },
    hairTail: {
      [BaseCharacterHairTail.Ponytail]: "baseCharacterHairTailPonyTail",
      [BaseCharacterHairTail.Twin]: "baseCharacterHairTailTwin",
      [BaseCharacterHairTail.DoubleBun]: "baseCharacterHairTailDoubleBun",
      [BaseCharacterHairTail.Braids]: "baseCharacterHairTailBraids",
      [BaseCharacterHairTail.BraidedPigtails]:
        "baseCharacterHairTailBraidedPigtails",
    },
    ears: {
      [BaseCharacterRace.Human]: "baseCharacterEarsHuman",
      [BaseCharacterRace.Elf]: "baseCharacterEarsElf",
      [BaseCharacterRace.Orc]: "baseCharacterEarsOrc",
    },
    raceTexture: {
      [BaseCharacterRace.Human]: {
        [BaseCharacterGender.Male]: "baseCharacterMaleDefaultRaceTexture",
        [BaseCharacterGender.Female]: "baseCharacterFemaleDefaultRaceTexture",
      },
      [BaseCharacterRace.Elf]: {
        [BaseCharacterGender.Male]: "baseCharacterMaleDefaultRaceTexture",
        [BaseCharacterGender.Female]: "baseCharacterFemaleDefaultRaceTexture",
      },
      [BaseCharacterRace.Orc]: {
        [BaseCharacterGender.Male]: "baseCharacterMaleOrcTexture",
        [BaseCharacterGender.Female]: "baseCharacterFemaleOrcTexture",
      },
    },
    moustache: {
      [BaseCharacterMoustache.Normal]: "baseCharacterMoustacheNormal",
    },
    beard: {
      [BaseCharacterBeard.Normal]: "baseCharacterBeardNormal",
    },
  },
};
